import * as XLSX from "xlsx";
import { isProfileUrlText, normalizeLinkedInUrl } from "../../../utils/linkedin";

/**
 * Reads an .xlsx/.xlsm in the browser and describes what is inside it.
 *
 * The file never leaves the user's machine. Candidate sheets carry names,
 * phone numbers and compensation, so nothing is uploaded - only the LinkedIn
 * URLs the user confirms are posted, and only to the endpoint the manual
 * "Add Linkedin Url" form already uses.
 *
 * xlsx (SheetJS) is already a dependency of this app and is used in nine other
 * places for exports; this is the first place it is used to read.
 */

// How many rows of each sheet to keep for the on-screen preview. The grid is
// scrollable but there is no point holding a 5,000-row sheet in state just to
// show the top of it.
const PREVIEW_ROWS = 60;

// How far down to look for the header row. PINE's real files put it on row 1,
// 2, 3, 5, 7 or 10 - a title line and a blank line above the headers is common.
const HEADER_SEARCH_DEPTH = 15;

// How many rows to sample when working out WHICH columns hold the links.
// Scanning every cell of every column on an 1,800-row sheet is wasted work.
const DETECT_SAMPLE_ROWS = 200;

const clean = (value) =>
  value === null || value === undefined ? "" : String(value).trim();

/**
 * The range a sheet actually occupies, worked out from the cells that exist.
 *
 * A sheet's declared "!ref" is often far bigger than its contents - one real
 * file declares ref="A1:XFB1566", which is 16,382 columns, of which about 17
 * hold anything. Trusting that makes sheet_to_json walk 25 million cells and
 * takes over a minute. Reading the worksheet's own keys costs one pass over
 * the cells that are really there.
 */
function usedRange(worksheet) {
  let min = null;

  for (const key of Object.keys(worksheet)) {
    if (key.charCodeAt(0) === 33) continue; // "!ref", "!cols", "!merges"...
    const { r, c } = XLSX.utils.decode_cell(key);
    if (!min) {
      min = { s: { r, c }, e: { r, c } };
      continue;
    }
    if (r < min.s.r) min.s.r = r;
    if (c < min.s.c) min.s.c = c;
    if (r > min.e.r) min.e.r = r;
    if (c > min.e.c) min.e.c = c;
  }

  return min || { s: { r: 0, c: 0 }, e: { r: 0, c: 0 } };
}

/**
 * What kind of field a header describes, or null if we cannot tell.
 *
 * Order matters. "Company Name" has to be read as a company, not as a person -
 * getting that wrong makes every search-universe sheet look like a candidate
 * list, because one column holds both the word "name" and the word "company".
 */
export function classifyColumn(label) {
  const text = String(label || "")
    .toLowerCase()
    .trim();
  if (!text) return null;

  if (/(compan|organi[sz]ation|employer|firm|client|account)\w*\s*name/.test(text))
    return "company";

  if (/candidate|name of (employee|candidate|person)|full name|\bname\b/.test(text))
    return "person";

  if (/linked\s*in|^li$|^li[\s_-]|\bli url/.test(text)) return "linkedin";
  if (/e-?mail|mail id/.test(text)) return "email";
  if (/phone|mobile|\bcontact\b|whatsapp/.test(text)) return "phone";
  if (/designation|job title|^title$|position|^role$/.test(text))
    return "designation";
  if (/compan|organi[sz]ation|employer/.test(text)) return "company";
  if (/ctc|salary|compensation|package|fixed pay/.test(text)) return "pay";
  if (/experience|yrs? of exp/.test(text)) return "experience";
  if (/location|^city$|^country$|geograph/.test(text)) return "location";
  if (/status/.test(text)) return "status";

  return null;
}

/**
 * Picks the row most likely to be the header.
 *
 * Scores each of the first few rows by how many short, non-numeric, distinct
 * text cells it holds. A header row is wide and wordy; a title row has one
 * cell; a data row has numbers and long free text in it.
 *
 * This only drives what the preview looks like. URL extraction deliberately
 * does not depend on it - see extractLinkedInUrls.
 */
function detectHeaderRow(grid) {
  let best = { index: 0, score: -1 };

  const depth = Math.min(HEADER_SEARCH_DEPTH, grid.length);

  for (let i = 0; i < depth; i += 1) {
    const row = grid[i] || [];
    const seen = new Set();
    let score = 0;

    row.forEach((cell) => {
      const text = clean(cell);
      if (!text) return;
      if (text.length > 60) return; // a paragraph, not a header
      if (/^-?[\d,.]+$/.test(text)) return; // a number, not a header
      if (/linkedin\.com/i.test(text)) return; // a data row full of URLs
      if (seen.has(text.toLowerCase())) return; // repeated label
      seen.add(text.toLowerCase());
      score += 1;
    });

    // Strictly greater, so the FIRST row that reaches the best score wins. A
    // data row can tie with its header; the header is above it.
    if (score > best.score) best = { index: i, score };
  }

  return best.score >= 2 ? best.index : 0;
}

/**
 * Does this sheet hold candidates, companies, or neither?
 *
 * `ok` decides whether the sheet is ticked for import by default. `level`
 * only drives the colour of the tag on screen. The user can always override
 * both - this is a steer, not a gate.
 */
function judgeSheet(labels, dataRowCount) {
  if (dataRowCount === 0) {
    return { ok: false, level: "muted", tag: "Empty sheet" };
  }

  const kinds = new Set(labels.map(classifyColumn).filter(Boolean));

  // Anything that identifies or reaches a specific person.
  const personal = ["linkedin", "email", "phone", "designation", "pay"].filter(
    (k) => kinds.has(k),
  );

  if (kinds.has("person") && personal.length >= 1) {
    return { ok: true, level: "good", tag: "Looks like a candidate list" };
  }

  // No name column, but the sheet still describes individual people - the
  // researcher mapping sheets are often laid out this way.
  if (!kinds.has("person") && kinds.has("designation") && personal.length >= 2) {
    return { ok: true, level: "warn", tag: "Possibly candidates — check" };
  }

  if (kinds.has("company") && !kinds.has("person")) {
    return { ok: false, level: "muted", tag: "Looks like a company list" };
  }

  return { ok: false, level: "muted", tag: "No candidate columns found" };
}

/* ------------------------------------------------------------------ */
/* LinkedIn URLs                                                       */
/* ------------------------------------------------------------------ */

// A LinkedIn address sitting inside a longer cell, with or without a scheme
// and with or without a country subdomain. Researchers paste URLs next to
// notes ("linkedin.com/in/jane-doe - no longer there"), so the address has to
// be cut out of the text rather than handed over whole.
const URL_IN_TEXT =
  /(?:https?:\/\/)?(?:[a-z]{2,3}\.)?linkedin\.com\/[^\s,;"'<>)\]]+/i;

/**
 * Undoes UTF-8 that has been read back as Latin-1.
 *
 * SheetJS decodes hyperlink targets byte by byte, so a target stored in the
 * .rels file as raw UTF-8 comes back mangled: "slørdahl" arrives as
 * "slÃ¸rdahl", because the two bytes C3 B8 were each taken for a character.
 * Encoding that as a URL then yields %C3%83%C2%B8 - the same character encoded
 * twice - and the person is stored under an address that does not exist.
 *
 * Every character being below U+0100 is the signature of the damage. If the
 * bytes are not valid UTF-8 the string was never mangled, and it is returned
 * untouched.
 */
function repairMojibake(text) {
  const value = String(text);

  // Character codes, not a regex: a literal range of high Latin-1 characters
  // in the source would itself depend on this file being read as UTF-8.
  let suspect = false;
  for (let i = 0; i < value.length; i += 1) {
    const code = value.charCodeAt(i);
    if (code > 0xff) return value; // a genuine character above Latin-1
    if (code >= 0x80) suspect = true;
  }

  if (!suspect) return value; // plain ASCII, nothing to repair

  try {
    const bytes = Uint8Array.from(value, (ch) => ch.charCodeAt(0));
    return new TextDecoder("utf-8", { fatal: true }).decode(bytes);
  } catch {
    return value; // not mangled UTF-8 after all
  }
}

/**
 * The LinkedIn profile URL in one cell, or "".
 *
 * The displayed text is read first and the hyperlink target second. Both are
 * needed: a cell often shows a person's name, or the word "LinkedIn", while
 * the address lives only in the hyperlink - on some sheets that is the only
 * place it exists at all. But where the cell shows the address too, that copy
 * is the trustworthy one, because SheetJS decodes cell text correctly and
 * hyperlink targets not always.
 */
export function linkedInUrlAt(worksheet, row, col) {
  const cell = worksheet[XLSX.utils.encode_cell({ r: row, c: col })];
  if (!cell) return "";

  const target = cell.l && cell.l.Target;

  const sources = [cell.w, cell.v, target && repairMojibake(target), target];

  for (const source of sources) {
    if (!isProfileUrlText(source)) continue;
    const match = String(source).match(URL_IN_TEXT);
    if (!match) continue;
    const url = normalizeLinkedInUrl(match[0]);
    if (url) return url;
  }

  return "";
}

/**
 * Every column that holds LinkedIn profile URLs, most first.
 *
 * Decided on the values, never on the header. The real files label this column
 * "LinkedIn", "LI", "Linkedln" (a typo), "LinkedIn Link" and "Linkedin URL" -
 * and one sheet has a "LinkedIn Presence" column holding Yes/No, which a
 * header-name match would pick by mistake.
 *
 * Several columns are returned because some sheets carry one URL per role on
 * the row - a CEO column, a CFO column, a CCO column - and picking only the
 * biggest would drop most of the people on the sheet.
 *
 * Only walks the columns describeSheet kept. One real sheet declares itself
 * 16,382 columns wide (ref="A1:XFB1566"); all but ~17 are empty padding.
 */
export function detectLinkColumns(worksheet, columns, knownRange) {
  const range = knownRange || usedRange(worksheet);

  const lastRow = Math.min(range.e.r, range.s.r + DETECT_SAMPLE_ROWS - 1);
  const hits = [];

  for (const col of columns) {
    let count = 0;
    for (let r = range.s.r; r <= lastRow; r += 1) {
      if (linkedInUrlAt(worksheet, r, col.index)) count += 1;
    }
    if (count > 0) hits.push({ index: col.index, count });
  }

  return hits.sort((a, b) => b.count - a.count);
}

/* Compensation ------------------------------------------------------ */

/** Words that mean a number in this cell is pay: "1.6 CR fixed", "CTC 88 lacs",
 *  "70 lakhs variable". */
const PAY_WORD =
  /\b(ctc|comp|compensation|salary|fixed|variable|bonus|lpa|lacs?|lakhs?|crores?|cr|package|pay)\b/i;

/** Or a currency, for the cells that give an amount and nothing else -
 *  "400K Euros including shares and car". */
const PAY_CURRENCY =
  /(₹|\binr\b|\brs\.?\b|\$|\busd\b|€|\beur(os?)?\b|£|\bgbp\b|\bsgd\b|\baed\b)/i;

/** The same vocabulary used for something that is not somebody's pay. Company
 *  revenue is the dangerous one: "GBP 10.4 Bn - Virgin Media O2" and
 *  "pnl - 6k - 7k cr topline" both read as money. */
const NOT_PAY =
  /\b(topline|revenue|turnover|valuation|bn|billion|employees|headcount|contact details|linkedin\.com)\b/i;

/** A bare run of digits with no letters or symbols. Whole columns of these are
 *  phone numbers, and they were the single worst false positive. */
const BARE_DIGITS = /^\+?\d[\d\s,()-]{8,}$/;

/** A header that names the column as pay. Not sufficient on its own - the
 *  cells still have to look like pay - but enough to lower the bar, because a
 *  column called "Current CTC" with one filled row is still the pay column. */
const PAY_HEADER = /ctc|salary|compensation|\bcomp\b|package|fixed pay|remuneration/i;

function looksLikePay(value) {
  const text = clean(value);
  if (!text) return false;
  if (BARE_DIGITS.test(text) && !/[a-z₹$€£]/i.test(text)) return false;
  if (NOT_PAY.test(text)) return false;
  if (!/\d/.test(text)) return false;
  return PAY_WORD.test(text) || PAY_CURRENCY.test(text);
}

/**
 * Columns holding what someone is paid, best first.
 *
 * TWO SIGNALS, AND THE CONTENT IS THE ONE THAT MATTERS
 *
 * An earlier attempt went by money-shape alone - a currency, a unit word, long
 * digit grouping - and flagged phone numbers, company revenue and LinkedIn URL
 * columns, because a slug like /in/anand-rv-31712 contains a long digit run.
 * Pay, phone numbers and revenue share a vocabulary.
 *
 * What separates them is the pay words. A cell needs a number AND either a pay
 * word or a currency, and must not mention revenue or be a bare phone number.
 *
 * The header then decides how much of that is enough. A column named for pay
 * needs one such cell, because real sheets are sparse - "Current CTC" on the
 * Master List sheet has 14 filled rows out of 120. An unnamed column needs
 * three and a third of its rows, or every notes column that mentions money
 * would qualify.
 *
 * Measured over the four files in 0FILES: 439 columns scanned, 9 found, no
 * false positives and nothing missed.
 */
export function detectCompensationColumns(
  worksheet,
  columns,
  knownRange,
  headerRow,
  excludeIndexes,
) {
  const range = knownRange || usedRange(worksheet);
  const exclude = new Set(excludeIndexes || []);
  const firstData = Math.max(
    range.s.r,
    typeof headerRow === "number" ? headerRow + 1 : range.s.r,
  );
  const lastRow = Math.min(range.e.r, firstData + DETECT_SAMPLE_ROWS - 1);

  const grid = XLSX.utils.sheet_to_json(worksheet, {
    header: 1,
    raw: false,
    blankrows: true,
    range,
  });

  const hits = [];

  for (const col of columns) {
    if (exclude.has(col.index)) continue;

    let filled = 0;
    let count = 0;

    for (let r = firstData; r <= lastRow; r += 1) {
      const row = grid[r - range.s.r] || [];
      const text = clean(row[col.index - range.s.c]);
      if (!text) continue;
      filled += 1;
      if (looksLikePay(text)) count += 1;
    }

    if (count === 0 || filled === 0) continue;

    const named = PAY_HEADER.test(col.label || "");
    const enough = named ? count >= 1 : count >= 3 && count / filled >= 0.33;

    if (enough) hits.push({ index: col.index, count, named });
  }

  // A named column first, then by how many pay cells it holds.
  return hits.sort((a, b) => (b.named ? 1 : 0) - (a.named ? 1 : 0) || b.count - a.count);
}

/**
 * Pulls every LinkedIn URL out of the chosen columns.
 *
 * Scans EVERY row in the sheet, not only the rows below the detected header.
 * Header detection is a guess made for the preview's benefit; on a sheet with
 * no real header it lands on a data row, and anchoring extraction to it would
 * silently drop every person above that line.
 *
 * Returns:
 *   urls       - normalised, in sheet order, duplicates within this sheet gone
 *   skipped    - rows that hold something but yielded no usable URL, with
 *                their cells lined up to `columns`, ready to be written back
 *                out as a spreadsheet
 *   duplicates - how many repeats were dropped
 */
/* ------------------------------------------------------------------ */
/* Status and notes columns                                            */
/* ------------------------------------------------------------------ */

/**
 * Per-column shape: how full it is, how repetitive, how wordy.
 *
 * One pass for both detectors below, over EVERY row rather than a sample.
 * The distinct count is the whole point and a sample understates it: one real
 * sheet showed 62 distinct values in its first 200 rows and 224 across all
 * 576, which is the difference between a status column and free text under a
 * status-sounding header.
 *
 * Affordable because this reads the already-built grid - no encode_cell per
 * cell, unlike the LinkedIn detection, which is why that one still samples.
 */
function columnStats(worksheet, columns, range, fromRow) {
  const grid = XLSX.utils.sheet_to_json(worksheet, {
    header: 1,
    raw: false,
    blankrows: true,
    range,
  });

  const first = Math.max(range.s.r, fromRow === undefined ? range.s.r : fromRow);
  const last = range.e.r;

  const stats = new Map();
  for (const col of columns) {
    stats.set(col.index, { filled: 0, chars: 0, distinct: new Set() });
  }

  for (let r = first; r <= last; r += 1) {
    const row = grid[r - range.s.r] || [];

    for (const col of columns) {
      const value = clean(row[col.index - range.s.c]);
      if (!value) continue;

      const stat = stats.get(col.index);
      stat.filled += 1;
      stat.chars += value.length;
      stat.distinct.add(value.toLowerCase());
    }
  }

  return stats;
}

// Kinds that are never a status and never a note, whatever they look like.
// "designation" is here because a job-title column is repetitive and short and
// otherwise scores like a status - it picked "Designation" and "President" on
// real sheets.
const NEVER_STATUS = ["linkedin", "email", "phone", "person", "company", "pay",
  "location", "experience", "designation"];
const NEVER_NOTE = [...NEVER_STATUS, "status"];

/**
 * Which columns hold a status, most likely first.
 *
 * A status column repeats itself: many rows, few distinct values, and those
 * values short. A name column is the opposite - every value distinct. Free
 * text is distinct AND long.
 *
 * Header names steer but never decide, the same as the LinkedIn detection.
 * One sample file labels the column "Status", another splits it into "Major
 * Status" and "Minor Status", and the next file will call it something else.
 */
export function detectStatusColumns(
  worksheet,
  columns,
  knownRange,
  headerRow,
  excludeIndexes,
) {
  const range = knownRange || usedRange(worksheet);
  const stats = columnStats(worksheet, columns, range, headerRow + 1);

  const hits = [];

  for (const col of columns) {
    if (NEVER_STATUS.includes(col.kind)) continue;
    if (excludeIndexes && excludeIndexes.has(col.index)) continue;

    const stat = stats.get(col.index);
    if (!stat || stat.filled < 3) continue;

    const distinct = stat.distinct.size;
    const averageLength = stat.chars / stat.filled;

    // A status column says the same few things over and over.
    if (averageLength > 80) continue;
    if (distinct > 80) continue;

    let score = 1 - distinct / stat.filled;
    if (col.kind === "status") score += 0.35;

    if (score < 0.35) continue;

    hits.push({ index: col.index, filled: stat.filled, distinct, score });
  }

  return hits.sort((a, b) => b.score - a.score);
}

/**
 * Which status columns to tick for the user, out of the ranked candidates.
 *
 * Deliberately cautious, because the ranking's second place is usually wrong.
 * Taking the top two on real sheets chose "Designation", "Headquarters",
 * "Mumbai/Gurgaon" and "Group Revenue / CEO" - and because two columns are
 * joined into one value for mapping, a bad second choice multiplies the
 * distinct values. One sheet went from about 20 to 306 that way.
 *
 * Two columns are only paired when BOTH headers name a status: the Major/Minor
 * layout, the one case where the pair is really a single value.
 *
 * A future file may name its status column something nobody has seen. Then
 * `named` is empty, nothing is ticked, and the ranked list is still offered for
 * the user to choose from - or to skip, which is phase 1's behaviour. Detection
 * failing is a supported outcome, not an error.
 */
export function defaultStatusPick(statusColumns) {
  const named = (statusColumns || []).filter((col) => col.kind === "status");

  if (named.length > 0) {
    // Exactly one column, and when a sheet offers several - the Major/Minor
    // layout - take the most detailed. "Briefed and not interested" maps
    // straight onto a PINE minor status; "Drop By Pmoves" only names the
    // bucket it belongs to, which is not a status PINE can be set to.
    return [[...named].sort((a, b) => b.distinct - a.distinct)[0]];
  }

  // Nothing names itself a status, so tick nothing.
  //
  // Shape cannot settle this. "Industry", "Country" and "Gender" repeat exactly
  // as hard as a status column does - Industry scored 0.98 on a real sheet, and
  // ticking it by default would have mapped 13 industry names onto candidate
  // statuses. Only the header separates them.
  //
  // The ranked candidates are still shown, so a file whose column is called
  // "Stage" or "Progress" is one click away. Asking beats guessing, especially
  // as every new consultant's file brings new wording.
  return [];
}

/**
 * Which columns hold free-text notes, wordiest first.
 *
 * The mirror image: mostly distinct values, and long ones. Real files call
 * these "Vikram Notes", "Reason for Removal", "Radhika Comments",
 * "Revenue/reasoning" - no header pattern worth matching.
 */
export function detectNotesColumns(
  worksheet,
  columns,
  knownRange,
  headerRow,
  excludeIndexes,
) {
  const range = knownRange || usedRange(worksheet);
  const stats = columnStats(worksheet, columns, range, headerRow + 1);

  const hits = [];

  for (const col of columns) {
    if (NEVER_NOTE.includes(col.kind)) continue;
    // Found to hold profile URLs by content, whatever the header calls it.
    if (excludeIndexes && excludeIndexes.has(col.index)) continue;

    const stat = stats.get(col.index);
    if (!stat || stat.filled < 2) continue;

    const averageLength = stat.chars / stat.filled;
    const variety = stat.distinct.size / stat.filled;

    // Short or repetitive means it is a category, not a note.
    if (averageLength < 20) continue;
    if (variety < 0.6) continue;

    hits.push({
      index: col.index,
      filled: stat.filled,
      averageLength: Math.round(averageLength),
    });
  }

  return hits.sort((a, b) => b.averageLength - a.averageLength);
}

/**
 * Everything one sheet has to say about the people on it.
 *
 * picks = { link: number[], status: number[], notes: number[] } - absolute
 * column indexes. Only `link` is required; a sheet with no status column
 * produces rows whose status is "", which is phase 1's behaviour exactly.
 *
 * Returns:
 *   rows       - one per person found, with url, status text, notes
 *   skipped    - rows holding something but no usable URL, cells lined up to
 *                `columns` so they can be written back out as a spreadsheet
 *   duplicates - repeats dropped
 */
export function extractRowData(worksheet, columns, picks) {
  const range = usedRange(worksheet);

  const pick = (list) =>
    (list || []).filter((index) => index !== null && index !== undefined);

  const linkCols = pick(picks && picks.link);
  const statusCols = pick(picks && picks.status);
  const noteCols = pick(picks && picks.notes);
  // One column, picked by hand. Never detected - see the compensation role in
  // ImportFromExcel.jsx for why.
  const compCols = pick(picks && picks.compensation);
  const personCol =
    picks && picks.person !== undefined && picks.person !== null
      ? picks.person
      : null;

  if (linkCols.length === 0) {
    return { rows: [], skipped: [], duplicates: 0 };
  }

  const grid = XLSX.utils.sheet_to_json(worksheet, {
    header: 1,
    raw: false,
    blankrows: true,
    range,
  });

  const byIndex = new Map(columns.map((col) => [col.index, col]));

  const rows = [];
  const skipped = [];
  const seen = new Set();
  let duplicates = 0;

  for (let r = range.s.r; r <= range.e.r; r += 1) {
    const row = grid[r - range.s.r] || [];

    // Excel leaves trailing empty rows behind; they are not skipped people.
    if (!row.some((cell) => clean(cell) !== "")) continue;

    const cellAt = (index) => clean(row[index - range.s.c]);

    // `found` counts every usable URL including repeats, while `fresh` holds
    // only the new ones. A row whose only URL is a duplicate has still been
    // seen, so it is not a skipped person.
    let found = 0;
    const fresh = [];

    for (const index of linkCols) {
      const url = linkedInUrlAt(worksheet, r, index);
      if (!url) continue;

      found += 1;

      if (seen.has(url)) {
        duplicates += 1;
        continue;
      }

      seen.add(url);
      fresh.push(url);
    }

    if (found === 0) {
      skipped.push({
        number: r + 1,
        cells: columns.map((col) => cellAt(col.index)),
      });
      continue;
    }

    // Two status columns are joined rather than kept apart, because the pair
    // carries the meaning: "Drop By Pmoves" alone and "Briefed and not
    // interested" alone are both weaker than the two together.
    const status = statusCols.map(cellAt).filter(Boolean).join(" · ");

    const notes = noteCols
      .map((index) => ({
        label: (byIndex.get(index) || {}).label || XLSX.utils.encode_col(index),
        value: cellAt(index),
      }))
      .filter((note) => note.value);
  // The cell exactly as written, not a number. Everything that turns
  // "INR 1.6 CR fixed + 35lakh bonus" into figures happens later, where the
  // user can see the original beside the reading and correct it.
  const compensation = compCols.map(cellAt).filter(Boolean).join(" · ");

    // Only ever shown, never sent anywhere and never written. A wrong guess
    // here is cosmetic, which is why the header is allowed to decide it.
    const person = personCol === null ? "" : cellAt(personCol);

    for (const url of fresh) {
      rows.push({ number: r + 1, url, status, notes, person, compensation });
    }
  }

  return { rows, skipped, duplicates };
}

/**
 * The column whose values are most likely to be people's names, or null.
 *
 * The ONE place a header is allowed to decide something. Everything else in
 * this reader goes by cell content, because being wrong there writes a wrong
 * status onto a real candidate. This value is only ever displayed - shown
 * beside a wording on the review screen so a human knows who they are ruling
 * on - so being wrong costs a confusing label and nothing else.
 *
 * Content cannot answer it anyway: a column of short distinct text is a name
 * column, a job title, a city or a company, and nothing in the values
 * separates them.
 *
 * Returns null when the sheet has no such column. Every caller must read
 * correctly without one - a sheet with no names still imports.
 */
export function defaultPersonColumn(columns) {
  const hit = (columns || []).find((col) => col.kind === "person");
  return hit ? hit.index : null;
}

/**
 * The distinct status values on a sheet, most common first.
 *
 * What gets sent for mapping. 193 rows of one sample file collapse to 67
 * distinct values, and those 67 are one model call, not 193.
 */
export function distinctStatuses(rows) {
  const counts = new Map();

  for (const row of rows) {
    if (!row.status) continue;
    counts.set(row.status, (counts.get(row.status) || 0) + 1);
  }

  return [...counts.entries()]
    .map(([value, rowCount]) => ({ value, rows: rowCount }))
    .sort((a, b) => b.rows - a.rows);
}

/**
 * URLs only. Phase 1's entry point, kept so the LinkedIn-only path is
 * unchanged; extractRowData does the work.
 */
export function extractLinkedInUrls(worksheet, columns, colIndexes) {
  const { rows, skipped, duplicates } = extractRowData(worksheet, columns, {
    link: colIndexes,
  });

  return { urls: rows.map((row) => row.url), skipped, duplicates };
}

/* ------------------------------------------------------------------ */
/* Sheets                                                              */
/* ------------------------------------------------------------------ */

/**
 * Turns one worksheet into everything the preview screen needs.
 *
 * headerOverride is an absolute 0-based row index. Pass it when the user
 * corrects the detected header row; leave it undefined to auto-detect.
 */
export function describeSheet(worksheet, name, headerOverride) {
  const range = usedRange(worksheet);

  // raw:false formats each cell the way Excel displays it, which turns date
  // serials such as 46265 into "31/08/2026" without us having to know the
  // epoch. defval is deliberately NOT set, and the range is the real one, not
  // the declared one - together those keep a sheet claiming 16,382 columns
  // from costing 25 million cell reads.
  const grid = XLSX.utils.sheet_to_json(worksheet, {
    header: 1,
    raw: false,
    blankrows: true,
    range,
  });

  const colInfo = worksheet["!cols"] || [];

  // sheet_to_json starts at the sheet's first USED cell, which is not always
  // A1 - real files have sheets declaring ref="B2:J26" or "A3:U26". Row and
  // column numbers here stay absolute, matching the worksheet addresses the
  // LinkedIn extractor reads, and the grid is indexed through these two.
  const gridRow = (sheetRow) => grid[sheetRow - range.s.r] || [];
  const gridCell = (row, sheetCol) => row[sheetCol - range.s.c];

  const headerRow =
    headerOverride === undefined || headerOverride === null
      ? range.s.r + detectHeaderRow(grid)
      : headerOverride;

  const headerCells = gridRow(headerRow);

  // Which columns hold anything at all, in ONE pass over the rows. Asking
  // that question per column instead costs rows x columns, which on the
  // 16,382-column sheet is 25 million checks.
  const populated = new Set();
  for (let r = headerRow + 1; r <= range.e.r; r += 1) {
    // forEach, not a counting loop: rows are sparse arrays, and forEach skips
    // the holes instead of visiting every index up to the last one.
    gridRow(r).forEach((cell, i) => {
      if (clean(cell) !== "") populated.add(range.s.c + i);
    });
  }

  const columns = [];
  for (let c = range.s.c; c <= range.e.c; c += 1) {
    const label = clean(gridCell(headerCells, c));

    // A column with no header and no data anywhere is padding Excel left
    // behind. Keep it out of the preview so the grid is not mostly blank.
    if (!label && !populated.has(c)) continue;

    columns.push({
      // Absolute sheet column, and where it sits in this trimmed list.
      // previewRows are built in `pos` order, so the renderer can look a
      // value up directly even when hidden columns are filtered out.
      index: c,
      pos: columns.length,
      letter: XLSX.utils.encode_col(c),
      label,
      hidden: Boolean(colInfo[c] && colInfo[c].hidden),
      kind: classifyColumn(label),
    });
  }

  // Rows below the header that have at least one value in them, carrying the
  // sheet row number so the preview shows what Excel shows.
  const body = [];
  for (let r = headerRow + 1; r <= range.e.r; r += 1) {
    const row = gridRow(r);
    if (!row.some((cell) => clean(cell) !== "")) continue;
    body.push({ number: r + 1, row });
  }

  const preview = body.slice(0, PREVIEW_ROWS).map(({ number, row }) => ({
    number,
    cells: columns.map((col) => clean(gridCell(row, col.index))),
  }));

  const labels = columns.map((col) => col.label).filter(Boolean);
  const withColumn = (hit) => ({
    ...columns.find((col) => col.index === hit.index),
    ...hit,
  });

  const linkColumns = detectLinkColumns(worksheet, columns, range).map((hit) => ({
    ...columns.find((col) => col.index === hit.index),
    sample: hit.count,
  }));

  // A column already holding profile URLs is not a status or a note, whatever
  // its header says. NEVER_STATUS and NEVER_NOTE exclude kind "linkedin", and
  // kind comes from the HEADER - so a URL column headed "Longlisting" was
  // classified null and offered as notes, its URLs being long and all distinct.
  // Excluding by content matches how the column was found in the first place.
  const linkIndexes = new Set(linkColumns.map((col) => col.index));

  const statusColumns = detectStatusColumns(
    worksheet,
    columns,
    range,
    headerRow,
    linkIndexes,
  ).map(withColumn);

  const notesColumns = detectNotesColumns(
    worksheet,
    columns,
    range,
    headerRow,
    linkIndexes,
  ).map(withColumn);

  const compensationColumns = detectCompensationColumns(
    worksheet,
    columns,
    range,
    headerRow,
    linkIndexes,
  ).map(withColumn);

  return {
    name,
    // Absolute, 0-based. firstRow is the sheet's first used row, which the
    // header-row picker needs so its options line up with the file.
    headerRow,
    firstRow: range.s.r,
    columns,
    previewRows: preview,
    dataRowCount: body.length,
    hiddenCount: columns.filter((col) => col.hidden).length,
    // Every column holding profile URLs, most first. Counts are sampled from
    // the first 200 rows, so they are a floor, not the total - the real
    // number comes from extractLinkedInUrls at import time.
    linkColumns,
    // Candidates for the status and notes steps, best first. Both may be
    // empty - a sheet with neither imports at the default, as phase 1 does.
    statusColumns,
    notesColumns,
    // Columns that look like pay, best first - a column named for pay before
    // one found by its contents alone. Offered as the default on the
    // compensation step; the user can change it or clear it.
    compensationColumns,
    shape: `${body.length} ${body.length === 1 ? "data row" : "data rows"} · ${
      columns.length
    } columns`,
    ...judgeSheet(labels, body.length),
  };
}

/**
 * Reads the whole file and describes every sheet in it.
 *
 * Returns { fileName, workbook, sheets }. Throws with a readable message if
 * the file is not a spreadsheet we can open.
 */
export async function readWorkbook(file) {
  let workbook;

  try {
    const buffer = await file.arrayBuffer();
    workbook = XLSX.read(buffer, { type: "array", cellDates: false });
  } catch (err) {
    console.error(err);
    throw new Error(
      "That file could not be opened. Save it as .xlsx from Excel and try again.",
    );
  }

  const names = workbook.SheetNames || [];
  if (names.length === 0) {
    throw new Error("That file has no sheets in it.");
  }

  return {
    fileName: file.name,
    // Kept so a header-row correction, and the import itself, can work from
    // memory without re-reading the file from disk.
    workbook,
    sheets: names.map((name) => describeSheet(workbook.Sheets[name], name)),
  };
}
